//
//  components/ui/Footer/Footer.tsx
//  TownLIT
//
//  Created by Hossein Sakkaki on 2026-10-19.
//  Last Update by Hossein Sakkaki on 2026-10-19.
//

"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import {
  selectTermsAndPolicies,
  selectMainLoading,
  selectMainError,
} from "../../../redux/selectors/main/mainSelectors";
import "./Footer.css";

const useInviteCode =
  String(process.env.NEXT_PUBLIC_USE_INVITE_CODE ?? "")
    .trim()
    .toLowerCase() === "true";

const Footer = () => {
  const termsAndPolicies = useAppSelector(selectTermsAndPolicies);
  const isLoading = useAppSelector(selectMainLoading).fetchTerms;
  const error = useAppSelector(selectMainError);

  const footerPolicies = termsAndPolicies.filter(
    (term) =>
      term.is_active &&
      term.show_in_footer === true &&
      (term.display_location === "footer" || term.display_location === "both"),
  );

  const leftColumnPolicies = footerPolicies.filter(
    (term) => term.footer_column === "left",
  );

  const rightColumnPolicies = footerPolicies.filter(
    (term) => term.footer_column === "right" || !term.footer_column,
  );

  const [isInAppShell, setIsInAppShell] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.startsWith("android-app://");

    setIsInAppShell(!!isStandalone);
  }, []);

  return (
    <footer className="footer-container">
      <div className="footer-top">
        <div className="footer-support">
          {!isInAppShell && (
            <Link
              href="/app"
              className="footer-support-link"
              title="Get TownLIT on your device"
            >
              Get TownLIT
            </Link>
          )}

          <Link
            href="/wayhood"
            className="footer-support-link"
            title="Discover Wayhood, the digital category introduced by TownLIT"
          >
            What Is a Wayhood?
          </Link>

          <Link
            href="/townlit-official/support"
            className="footer-support-link"
            title="See how you can support TownLIT"
          >
            Ways to Support
          </Link>

          <Link
            href="/townlit-official/help-support"
            className="footer-support-link"
            title="Get help or contact TownLIT Support"
          >
            Help &amp; Support
          </Link>

          <Link
            href="/townlit-official/company"
            className="footer-support-link"
            title="Learn about LITCore Software Inc. and TownLIT"
          >
            Company
          </Link>

          {useInviteCode && (
            <Link
              href="/townlit-official/request-access"
              className="footer-support-link"
            >
              Be Invited
            </Link>
          )}

          {leftColumnPolicies.map((term) => (
            <Link
              key={term.slug}
              href={`/townlit-official/foundation/${term.slug}`}
              className="footer-support-link"
            >
              {term.title}
            </Link>
          ))}
        </div>

        <div className="footer-terms">
          {isLoading && (
            <p className="official-footer-message">
              Loading official documents...
            </p>
          )}

          {error && (
            <p className="official-footer-message">
              Failed to load official policies.
            </p>
          )}

          {rightColumnPolicies.map((term) => (
            <Link
              key={term.slug}
              href={`/townlit-official/foundation/${term.slug}`}
              className="footer-link"
            >
              {term.title}
            </Link>
          ))}
        </div>
      </div>

      <div className="footer-copy">
        <p>
          © 2022–{new Date().getFullYear()} LITCore Software Inc. All
          rights reserved.
        </p>
        <p>
          TownLIT<sup>™</sup> — established in 2022 and operated by LITCore
          Software Inc.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
